import React, { useEffect, useState } from "react";
import {
  Box,
  Button,
  ButtonBase,
  FormHelperText,
  InputLabel,
  Skeleton,
  Tooltip,
  Typography,
} from "@mui/material";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import CustomInput from "global/components/CustomInput/CustomInput";
import CustomDateRangePicker from "global/components/CustomDateRangePicker/CustomDateRangePicker";
import { DateRange } from "models/interfaces";
import {
  getConfigurationData,
  handleEnableConfigUpdateBtn,
  handleUpdate,
} from "screens/MySignRequests/MySignRequestHelper";
import { isTruthy } from "helpers/methods";
import { selectEmail } from "redux/authSlice";
import { useAppSelector } from "hooks/use-app-select";
import { useTitle } from "utils/UseTitle";
import title from "global/constants/TitleConstants";
import strings from "global/constants/StringConstants";
import { useFeatureState, FeatureState } from "hooks/use-feature";
import FeatureKeys from "global/constants/FeatureKeys";
import FeatureBanner from "global/components/FeatureBanner/FeatureBanner";
import SettingsLayout from "../components/SettingsLayout";
import delegationStyles from "./Delegation.styles";
import delegationTokens from "./Delegation.tokens";
import { DateRangeCalendarIcon } from "./components/DelegationIcons";

const styles = delegationStyles;

const Delegation = () => {
  useTitle(title.DELEGATION);
  const userEmail: string = useAppSelector(selectEmail);

  const delegationState = useFeatureState(FeatureKeys.ENVELOPE_DELEGATION);
  const isDelegationAvailable =
    delegationState.status === strings.FEATURE_STATUS_AVAILABLE;

  const getFeatureTooltip = (state: FeatureState): string => {
    if (state.status === strings.FEATURE_STATUS_NOT_IN_PLAN)
      return strings.FEATURE_GATE_UPSELL_TOOLTIP;
    if (state.status === strings.FEATURE_STATUS_ADMIN_DISABLED)
      return (
        state.disabledMessage ??
        strings.FEATURE_GATE_TEMPORARILY_UNAVAILABLE_TOOLTIP
      );
    return "";
  };

  const [loading, setLoading] = useState(false);
  const [openDateRangePicker, setOpenDateRangePicker] = useState<boolean>(false);

  // Only delegation-relevant fields
  const [configurationFields, setConfigurationFields] = useState<any>({
    allowSignatureDelegation: false,
    delegateName: "",
    delegateEmail: "",
    delegateStartDate: "",
    delegateEndDate: "",
  });

  const [previousValues, setPreviousValues] = useState<any>({
    allowSignatureDelegation: false,
    delegateName: "",
    delegateEmail: "",
    delegateStartDate: "",
    delegateEndDate: "",
  });

  // btn3 is used for delegation; btn1/btn2 kept for handleUpdate signature compatibility
  const [updateButtonEnabled, setUpdateButtonEnabled] = useState({
    btn1: false,
    btn2: false,
    btn3: false,
  });

  // Required by handleUpdate signature even though not rendered for delegation
  const [errors, setErrors] = useState<any>({});
  const [autoExpErrors, setAutoExpErrors] = useState<any>({});

  const [autoDelegateErrors, setAutoDelegateErrors] = useState<any>({
    delegationEmail: "",
    delegationName: "",
    delegationDateRange: "",
  });

  useEffect(() => {
    if (userEmail) {
      getConfigurationData(
        setLoading,
        setConfigurationFields,
        setPreviousValues,
        userEmail,
      );
    }
  }, [userEmail]);

  const handleOnChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value, name } = event.target;
    setConfigurationFields((prev: any) => ({ ...prev, [name]: value }));
    handleEnableConfigUpdateBtn(
      name,
      value,
      setUpdateButtonEnabled,
      updateButtonEnabled,
      previousValues,
    );

    if (name === "delegateName") {
      setAutoDelegateErrors((prev: any) => ({
        ...prev,
        delegationName: isTruthy(value.trim()) ? "" : "Please enter name.",
      }));
    }

    if (name === "delegateEmail") {
      const trimmedEmail = value.trim();
      setAutoDelegateErrors((prev: any) => ({
        ...prev,
        delegationEmail: !isTruthy(trimmedEmail)
          ? "Please enter a email."
          : trimmedEmail.includes(",")
            ? "Please enter a single valid email"
            : strings.regex.test(trimmedEmail)
              ? ""
              : "Please enter a valid email",
      }));
    }
  };

  const handleToggleDelegation = () => {
    const checked = !configurationFields.allowSignatureDelegation;
    setConfigurationFields((prev: any) => ({
      ...prev,
      allowSignatureDelegation: checked,
    }));
    handleEnableConfigUpdateBtn(
      "allowSignatureDelegation",
      checked,
      setUpdateButtonEnabled,
      updateButtonEnabled,
      previousValues,
    );
  };

  const delegationTooltip = getFeatureTooltip(delegationState);
  const effectiveAllowDelegation =
    isDelegationAvailable && configurationFields.allowSignatureDelegation;

  const delegateEmailTrimmed = (configurationFields.delegateEmail || "").trim();
  const isAutoDelegationInvalid =
    effectiveAllowDelegation &&
    (!isTruthy((configurationFields.delegateName || "").trim()) ||
      !isTruthy(delegateEmailTrimmed) ||
      delegateEmailTrimmed.includes(",") ||
      !strings.regex.test(delegateEmailTrimmed) ||
      !isTruthy(configurationFields.delegateStartDate) ||
      !isTruthy(configurationFields.delegateEndDate));

  const isDateRangeEmpty =
    !isTruthy(configurationFields.delegateStartDate) &&
    !isTruthy(configurationFields.delegateEndDate);

  const getSkeleton = () => (
    <Box sx={styles.card}>
      <Box sx={styles.cardHeader}>
        <Box sx={styles.skeletonHeaderText}>
          <Skeleton
            variant="text"
            width={delegationTokens.skeleton.titleWidth}
            height={delegationTokens.skeleton.titleHeight}
          />
          <Skeleton
            variant="text"
            width="100%"
            height={delegationTokens.skeleton.descriptionHeight}
          />
          <Skeleton
            variant="text"
            width={delegationTokens.skeleton.descriptionWidth}
            height={delegationTokens.skeleton.descriptionHeight}
          />
        </Box>
        <Skeleton
          variant="rounded"
          width={delegationTokens.toggle.width}
          height={delegationTokens.toggle.height}
          sx={{
            ml: "auto",
            mt: delegationTokens.toggle.marginTop,
            flexShrink: 0,
            borderRadius: "999px",
          }}
        />
      </Box>
      <Box sx={styles.fieldGrid(true)}>
        {[0, 1].map((index) => (
          <Box key={index}>
            <Skeleton
              variant="text"
              width={delegationTokens.skeleton.labelWidth}
              height={delegationTokens.skeleton.labelHeight}
              sx={{ mb: delegationTokens.field.labelGap }}
            />
            <Skeleton
              variant="rounded"
              width="100%"
              height={delegationTokens.field.height}
              sx={{ borderRadius: delegationTokens.field.radius }}
            />
          </Box>
        ))}
        <Box sx={styles.fullWidthField}>
          <Skeleton
            variant="text"
            width={delegationTokens.skeleton.labelWidth}
            height={delegationTokens.skeleton.labelHeight}
            sx={{ mb: delegationTokens.field.labelGap }}
          />
          <Skeleton
            variant="rounded"
            width="100%"
            height={delegationTokens.field.height}
            sx={{ borderRadius: delegationTokens.field.radius }}
          />
        </Box>
      </Box>
      <Box sx={styles.buttonRow(true)}>
        <Skeleton
          variant="rounded"
          width={delegationTokens.skeleton.buttonWidth}
          height={delegationTokens.primaryButton.height}
          sx={{ borderRadius: delegationTokens.primaryButton.radius }}
        />
      </Box>
    </Box>
  );

  const getDelegationCard = () => (
    <Box sx={styles.card}>
      <Box sx={styles.cardHeader}>
        <Box sx={styles.cardHeaderText}>
          <Typography component="h2" sx={styles.cardTitle}>
            Automated Delegation
          </Typography>
          <Typography component="div" sx={styles.cardDescription}>
            While you're away, forward every signing request to someone you
            trust — they sign on your behalf for the period you choose.
          </Typography>
        </Box>
        <Tooltip
          title={delegationTooltip}
          disableHoverListener={isDelegationAvailable}
          arrow
          describeChild
        >
          <Box component="span" sx={styles.toggleWrapper}>
            <ButtonBase
              disableRipple
              role="switch"
              aria-checked={effectiveAllowDelegation}
              aria-label="Automated Delegation"
              disabled={!isDelegationAvailable}
              onClick={handleToggleDelegation}
              sx={styles.toggleTrack(effectiveAllowDelegation)}
            >
              <Box sx={styles.toggleKnob(effectiveAllowDelegation)} />
            </ButtonBase>
            {!isDelegationAvailable && (
              <LockOutlinedIcon sx={styles.toggleLock} />
            )}
          </Box>
        </Tooltip>
      </Box>

      {!isDelegationAvailable && (
        <Box sx={{ mb: 2 }}>
          <FeatureBanner
            state={delegationState}
            featureName="Automated delegation"
            variant="compact"
          />
        </Box>
      )}

      <Box sx={styles.fieldGrid(effectiveAllowDelegation)}>
        <CustomInput
          id="delegateName"
          label="Delegate Name"
          placeHolder="Who signs for you"
          name="delegateName"
          type="text"
          disabled={!effectiveAllowDelegation}
          onChange={handleOnChange}
          value={configurationFields.delegateName}
          error={
            !isTruthy(configurationFields.delegateName)
              ? autoDelegateErrors.delegationName
              : ""
          }
          labelSx={styles.fieldLabel}
          labelSpacing={delegationTokens.field.labelGap}
          sx={styles.textField}
        />
        <CustomInput
          id="delegateEmail"
          label="Delegate Email"
          placeHolder="name@company.com"
          name="delegateEmail"
          type="email"
          disabled={!effectiveAllowDelegation}
          onChange={handleOnChange}
          value={configurationFields.delegateEmail}
          error={autoDelegateErrors.delegationEmail}
          labelSx={styles.fieldLabel}
          labelSpacing={delegationTokens.field.labelGap}
          sx={styles.textField}
        />

        <Box sx={styles.fullWidthField}>
          <Box mb={delegationTokens.field.labelGap}>
            <InputLabel sx={styles.fieldLabel}>
              Delegation date range
            </InputLabel>
          </Box>
          <CustomDateRangePicker
            ariaLabel="Delegation date range"
            disableFuture={false}
            hideShortcuts
            minDate={new Date().toISOString().split("T")[0]}
            dateRange={{
              fromDate: configurationFields.delegateStartDate,
              toDate: configurationFields.delegateEndDate,
            }}
            openDateRangePicker={openDateRangePicker}
            setOpenDateRangePicker={setOpenDateRangePicker}
            setDateRange={(dateRange: DateRange) => {
              setConfigurationFields((prev: any) => ({
                ...prev,
                delegateStartDate: dateRange.fromDate,
                delegateEndDate: dateRange.toDate,
              }));
              handleEnableConfigUpdateBtn(
                "delegateStartDate",
                dateRange.fromDate?.toString() || "",
                setUpdateButtonEnabled,
                updateButtonEnabled,
                previousValues,
              );
              handleEnableConfigUpdateBtn(
                "delegateEndDate",
                dateRange.toDate?.toString() || "",
                setUpdateButtonEnabled,
                updateButtonEnabled,
                previousValues,
              );
            }}
            disabled={!effectiveAllowDelegation}
            sx={styles.dateRangeField(isDateRangeEmpty)}
            startIcon={
              <DateRangeCalendarIcon
                sx={styles.dateRangeIcon}
                color={delegationTokens.dateRange.iconColor}
              />
            }
          />
          {isDateRangeEmpty && autoDelegateErrors.delegationDateRange && (
            <FormHelperText error role="alert" sx={styles.fieldError}>
              {autoDelegateErrors.delegationDateRange}
            </FormHelperText>
          )}
        </Box>
      </Box>

      <Box sx={styles.buttonRow(effectiveAllowDelegation)}>
        <Tooltip
          title={delegationTooltip}
          disableHoverListener={isDelegationAvailable}
          arrow
          describeChild
        >
          <Box component="span" sx={{ display: "inline-flex" }}>
            <Button
              id="delegation_update_btn"
              variant="contained"
              disabled={
                !updateButtonEnabled.btn3 ||
                isAutoDelegationInvalid ||
                !isDelegationAvailable
              }
              endIcon={
                !isDelegationAvailable ? <LockOutlinedIcon /> : undefined
              }
              onClick={() =>
                handleUpdate(
                  "autoDelegation",
                  setLoading,
                  setPreviousValues,
                  errors,
                  setErrors,
                  autoExpErrors,
                  setAutoExpErrors,
                  configurationFields,
                  setUpdateButtonEnabled,
                  updateButtonEnabled,
                  autoDelegateErrors,
                  setAutoDelegateErrors,
                  userEmail,
                )
              }
              sx={styles.primaryButton}
            >
              Update
            </Button>
          </Box>
        </Tooltip>
      </Box>
    </Box>
  );

  return (
    <SettingsLayout activeTab="Delegation" title="Delegation">
      {loading ? getSkeleton() : getDelegationCard()}
    </SettingsLayout>
  );
};

export default Delegation;
